import React, { useState, useEffect, lazy, Suspense } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  CircularProgress,
  Box
} from '@material-ui/core'
import { ATTACHMENT_TYPES } from '../utils/attachmentUtils'
import { useAttachment } from '../contexts/AttachmentContext'
import { useAIThreadAttachment } from '../../../../api/denaliAssistant'
import Alert from '../../../../components/atoms/Alert'
import OpalHeading from '../../../../components/opal/OpalHeading'
import SydButton from '../../../../components/commonDesign/Button'
import { AttachmentPanel } from './AttachmentPanel'

// Lazy load viewers for better performance
const QueryResultsViewer = lazy(() => import('./viewers/QueryResultsViewer'))
const JSXViewer = lazy(() => import('./viewers/JSXViewer'))
const DQLViewer = lazy(() => import('./viewers/DQLViewer'))
const CSVViewer = lazy(() => import('./viewers/CSVViewer'))
const EmailViewer = lazy(() => import('./viewers/EmailViewer'))
const MarkdownViewer = lazy(() => import('./viewers/MarkdownViewer'))
const DefaultViewer = lazy(() => import('./viewers/DefaultViewer'))

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    padding: theme.spacing(4)
  },
  errorContainer: {
    padding: theme.spacing(3)
  },
  viewerContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  }
}))

export function AttachmentViewer () {
  const classes = useStyles()
  const { selectedAttachment, isOpen, closeAttachment } = useAttachment()
  const [viewMode, setViewMode] = useState('preview')

  // Determine if this is an inline attachment or needs fetching
  const isInlineAttachment = selectedAttachment?.inline && selectedAttachment?.data
  const needsRemoteFetch = !selectedAttachment?.inline && !!selectedAttachment?.threadId && !!selectedAttachment?.attachmentId

  // Use React Query for fetching non-inline attachments
  const { data: remoteData, error: fetchError, isLoading } = useAIThreadAttachment(
    selectedAttachment?.threadId,
    selectedAttachment?.attachmentId,
    {
      enabled: isOpen && needsRemoteFetch
    }
  )

  // Reset view mode when attachment changes
  useEffect(() => {
    setViewMode('preview')
  }, [selectedAttachment?.attachmentId])

  // Get the attachment data
  const attachmentData = isInlineAttachment
    ? selectedAttachment.data
    : remoteData?.attachment?.data || remoteData?.data || remoteData

  // Determine error state
  const error = fetchError?.message ||
    (selectedAttachment?.inline && !selectedAttachment?.data ? 'Attachment data is not available' : null)

  // Get the appropriate viewer component
  const getViewer = () => {
    // For remote attachments, show loading state
    if (needsRemoteFetch && isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }

    // Show errors
    if (error) {
      return (
        <Box className={classes.errorContainer}>
          <Alert severity='error'>
            <OpalHeading>Unable to load attachment</OpalHeading>
            {error}
          </Alert>
        </Box>
      )
    }

    // No data available
    if (!attachmentData) {
      return null
    }

    const viewerProps = {
      data: attachmentData,
      metadata: selectedAttachment
    }

    switch (selectedAttachment?.contentType) {
      case ATTACHMENT_TYPES.QUERY_RESULTS:
        return <QueryResultsViewer {...viewerProps} />

      case ATTACHMENT_TYPES.JSX_CODE:
        return (
          <JSXViewer
            {...viewerProps}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )

      case ATTACHMENT_TYPES.DQL_QUERY:
      case ATTACHMENT_TYPES.SQL_QUERY:
        return <DQLViewer {...viewerProps} />

      case ATTACHMENT_TYPES.CSV_DATA:
        return <CSVViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_EMAIL:
        return <EmailViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_AGENDA:
      case ATTACHMENT_TYPES.ARTIFACT_TALKING_POINTS:
      case ATTACHMENT_TYPES.ARTIFACT_PROPOSAL:
      case ATTACHMENT_TYPES.ARTIFACT_SUMMARY:
        return <MarkdownViewer {...viewerProps} />

      default:
        return <DefaultViewer {...viewerProps} />
    }
  }

  // Create header actions for JSX content
  const headerActions = selectedAttachment?.contentType === ATTACHMENT_TYPES.JSX_CODE ? (
    <>
      <SydButton
        size='sm'
        variant={viewMode === 'preview' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('preview')}
        icon='visibility'
      >
        Preview
      </SydButton>
      <SydButton
        size='sm'
        variant={viewMode === 'source' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('source')}
        icon='code'
      >
        Source
      </SydButton>
    </>
  ) : null

  return (
    <AttachmentPanel
      open={isOpen}
      onClose={closeAttachment}
      title={selectedAttachment?.title || 'Attachment'}
      width='600px'
      headerActions={headerActions}
    >
      <div className={classes.viewerContainer} key={selectedAttachment?.attachmentId}>
        <Suspense fallback={
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        }
        >
          {getViewer()}
        </Suspense>
      </div>
    </AttachmentPanel>
  )
}
